import { useCallback, useEffect, useRef } from "react";

// A tap that closes the sheet (endGesture below) unmounts it immediately on
// pointerup - but the same physical touch still fires the browser's own
// trailing "ghost click" a moment later (its usual mouse-compatibility
// event for a touch tap), at the same screen coordinates. By then the sheet
// is gone, so that click lands on whatever page content is now revealed
// underneath instead - on Schedule/Grids/Groups that's routinely a real
// match cell once you've scrolled down far enough that one sits right where
// the sheet's handle used to be, and clicking it reopens the sheet through
// the exact same "view this match" handler a deliberate tap uses. Read as
// "the sheet randomly won't close" (plus a jump back to the top, from that
// reopen's own scroll-to-top). Swallowing exactly one click globally, right
// after a tap-close, eats that ghost click wherever it lands - a single
// fix here instead of teaching every caller's own match-click handler
// (ScheduleTable.js, Bracket.js, BracketClassic.js, TournamentGroupTable.js,
// ...) to separately guard against it.
const GHOST_CLICK_GUARD_MS = 500;
const suppressNextClick = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
};
const swallowGhostClick = () => {
    document.addEventListener("click", suppressNextClick, { capture: true, once: true });
    setTimeout(() => {
        document.removeEventListener("click", suppressNextClick, { capture: true });
    }, GHOST_CLICK_GUARD_MS);
};

// Drag-to-resize bottom sheet, following the same shape as
// TournamentSwipeTabs' gesture handling (mutable ref per gesture, not
// state, so pointermove never triggers a render) - but on Pointer Events
// instead of touch-only, since this drags with a mouse on desktop too.
//
// The sheet only ever mounts already at FULL (as tall as it gets, but
// still stops below the page's own controls - not a full-screen takeover -
// so you can still switch stages/places or reach the header while browsing
// the match list at its tallest; see getFullHeight) - a caller triggers
// that mount itself (e.g. clicking a match row elsewhere on the page,
// BottomSheetPanel's own isOpen). There's no PEEK resting state to fall
// back to anymore (there used to be one, permanently poking up from the
// bottom edge even with nothing selected - see BottomSheetPanel.jsx's own
// comment on why that was removed): a tap on the handle, or dragging the
// sheet down far enough and releasing, both call onClose instead of
// settling at a smaller height. The drag itself still free-follows the
// pointer down to PEEK_HEIGHT_PX as a visual floor (so the sheet doesn't
// vanish mid-drag before the release decides open-vs-close), it just never
// rests there.
//
// Drives the sheet's own `height` directly rather than translateY-ing a
// permanently-tall box: the sheet docks straight to the viewport's bottom
// edge (`bottom: 0` in CSS, never changes, on top of the app's fixed
// bottom nav via z-index), so its bottom edge is already exactly where it
// should be at every size - growing/shrinking height is what "peek" vs
// "full" actually mean here. A translateY approach was tried first and
// doesn't work here either: sliding a 92vh-tall box mostly off-screen
// still leaves its opaque remainder covering whatever's at the very
// bottom of the viewport, because that remainder's visible edge is the
// viewport's physical bottom, not the box's own (translated-away) resting
// bottom.
//
// handleRef is a callback ref rather than a plain useRef: consumers may
// remount the sheet's content (e.g. switching stages/tabs), so listeners
// are attached exactly when the node appears instead of depending on some
// unrelated prop change to re-run an effect.
// Matches .modal-drag-handle-zone's own CSS height exactly - the peek
// state should show precisely the title/handle and nothing past it.
const PEEK_HEIGHT_PX = 40;
// Leaves a visible gap between the page's lowest control (stage select /
// place tabs) and the sheet's top edge at FULL, instead of the sheet
// touching it flush.
const CONTROLS_GAP_PX = 10;
// Grids' own place-tabs row (.tournament-stages) sits 2px closer to the
// sheet than the plain CONTROLS_GAP_PX elsewhere - a deliberate tighter
// gap for that row specifically, not a rounding fudge.
const GRIDS_CONTROLS_GAP_PX = 6;
// A saved match (winner/score) re-renders the whole draws/groups page twice
// in quick succession - once optimistically, once again when the server
// confirms - each capable of nudging the observed controls row's measured
// height by a stray sub-pixel with no real layout change. Reacting to that
// with the animated snap below (meant for a genuine tab-wrap-style change)
// reads as the whole sheet abruptly bouncing for a moment. Only a change at
// least this large is treated as real.
const RESIZE_JUMP_THRESHOLD_PX = 8;
const TAP_MAX_MOVEMENT_PX = 6;
const SNAP_TRANSITION = "height 0.28s cubic-bezier(0.32, 0.72, 0, 1)";

// Mirrors the sheet's own .bottom-sheet-fullscreen class onto <body> so
// plain page-level CSS (outside this sheet's own subtree, which is
// portaled to document.body) can react to it too:
// - locks the underlying page's own scroll while the sheet is FULL
//   (see body.bottom-sheet-full in CustomModal.css) - without this,
//   scrolling with the pointer over the visible strip of page content
//   above the sheet (e.g. near the stage selector) scrolled the Groups/
//   Grids table underneath instead of the sheet's own match list, which
//   also worked against the scroll-to-top/restore in openFull below.
// - hides Grids' view-mode toggle (.icon-dropdown) while the sheet is
//   FULL (see index.css) - it sits right next to the stage selector,
//   which the sheet already reserves clearance for, but there's no
//   reason to keep an unrelated control on screen once the sheet has
//   taken over.
const setFullscreenState = (el, isFull) => {
    if (el) el.classList.toggle("bottom-sheet-fullscreen", isFull);
    document.body.classList.toggle("bottom-sheet-full", isFull);
};

export const useBottomSheet = ({ expandSignal, scrollTargetId, onClose }) => {
    const sheetRef = useRef(null);
    const heightRef = useRef(PEEK_HEIGHT_PX);
    const gestureRef = useRef(null);
    const cleanupRef = useRef(null);
    const sheetInitCleanupRef = useRef(null);

    // FULL stops short of covering the page's own controls - not just the
    // fixed site header (z-index:50, above this sheet's default
    // z-index:10), but whichever of the page's own selectors sits lowest:
    // Grids' merged stage+place-tabs+view-toggle row (.tournament-stages -
    // place tabs "1 Місце"/"3-8 Місце" and the view toggle now live in the
    // same row as the stage picker, not a separate .grid-tabs-row below
    // it), or just the stage picker itself (.stage-tabs-wrap /
    // .stage-selector) on Groups/Schedule/Grids pages with only one level
    // of selector. Whichever is lowest already clears everything above it
    // too, since they're stacked in that order (.tournament-stages, when
    // present, IS the lowest - it's Grids' own row, sitting below the
    // outer stage picker TournamentDrawStages renders above it). This is
    // what makes those controls usable *while* the sheet is open - e.g.
    // tapping "3-8 Місце" to switch which bracket's matches the sheet is
    // showing - instead of the sheet just burying them. Falls back to the
    // header's own bottom edge where none of them render (a single-stage
    // tournament, where StageSelector's `stages.length === 0` early
    // return skips it entirely). Reading live rects (rather than
    // hardcoded heights) keeps this correct if any of them ever changes
    // height (e.g. wrapping tabs on a narrow screen).
    const getControlsElement = useCallback(() => {
        // .group-tabs-row (Groups' own "Всі групи"/A/B/C... row, below its
        // stage selector) is the lowest control on a Groups page when
        // present - checked first for the same reason .tournament-stages is
        // checked first on Grids pages below. The two never coexist (this
        // row only renders inside TournamentGroups.js), so there's no
        // ordering conflict between them.
        return document.querySelector(".group-tabs-row")
            || document.querySelector(".tournament-stages")
            // StageTabsScroll renders one or the other depending on whether
            // the pills currently fit (.stage-tabs-wrap) or overflowed into
            // the select fallback (.stage-selector) - both need to clear
            // the sheet the same way the old always-.stage-selector
            // StageSelector did.
            || document.querySelector(".stage-tabs-wrap")
            || document.querySelector(".stage-selector .custom_select")
            || document.querySelector(".main-header");
    }, []);

    const getControlsClearance = useCallback(() => {
        const el = getControlsElement();
        if (!el) return 0;
        const gap = el.classList.contains("tournament-stages") ? GRIDS_CONTROLS_GAP_PX : CONTROLS_GAP_PX;
        return Math.max(0, el.getBoundingClientRect().bottom + gap);
    }, [getControlsElement]);

    // sheetRef's own rect gives the sheet's bottom edge without
    // duplicating the CSS `bottom: 0` offset as a second magic number here
    // - now the viewport's own true bottom, so FULL runs down over the nav
    // too instead of stopping above it. FULL is the tallest the sheet
    // gets - not a full-screen takeover.
    const getFullHeight = useCallback(() => {
        const sheetBottom = sheetRef.current
            ? sheetRef.current.getBoundingClientRect().bottom
            : window.innerHeight;
        return Math.max(PEEK_HEIGHT_PX, Math.round(sheetBottom - getControlsClearance()));
    }, [getControlsClearance]);

    const applyHeight = useCallback((px, withTransition) => {
        const el = sheetRef.current;
        if (!el) return;
        el.style.transition = withTransition ? SNAP_TRANSITION : "none";
        el.style.setProperty("--sheet-height", `${px}px`);
    }, []);

    // getFullHeight measures the page's own controls (stage selector etc)
    // via a live getBoundingClientRect - which only lands below them if
    // those controls are still on screen. If the page was scrolled past
    // them first (e.g. scrolled down the groups table, then tapping a
    // match to expand the sheet), their rect sits above the viewport and
    // the clearance clamps to ~0, so the sheet stretches all the way to
    // the very top of the viewport instead of stopping under them. Scroll
    // back to the top before measuring/expanding so the calculation is
    // always done from the same, correct vantage point, and remember
    // where the user actually was so closing the sheet can return them
    // there (restored in setSheetNode's own unmount cleanup below, since
    // closing now means unmounting, not collapsing to a smaller height) -
    // e.g. after tapping a match row lower down the page to score it,
    // closing the sheet again should land back on that row instead of
    // leaving the page scrolled to the top.
    const savedScrollYRef = useRef(null);

    const clearanceObserverRef = useRef(null);
    const clearanceRafRef = useRef(null);

    // While FULL, the sheet's own height needs to keep tracking the
    // controls row it's clearing (getControlsElement) live, not just once
    // at the moment it opened - e.g. Grids' place tabs ("1 Місце"/"3-8
    // Місце", part of .tournament-stages) differ in count/wrap per bracket
    // stage, so switching stages *without* a remount (same TournamentGrids
    // instance, sheet staying open the whole time) can still change how
    // tall that row is. A ResizeObserver on the currently-cleared element
    // catches that and re-measures/re-applies FULL's height to match,
    // instead of leaving the sheet's top edge frozen at whatever the row's
    // height happened to be back when FULL was first entered.
    const watchClearance = useCallback(() => {
        if (clearanceObserverRef.current) {
            clearanceObserverRef.current.disconnect();
            clearanceObserverRef.current = null;
        }
        if (clearanceRafRef.current) {
            cancelAnimationFrame(clearanceRafRef.current);
            clearanceRafRef.current = null;
        }
        const el = getControlsElement();
        if (!el) return;

        // A stage switch can settle the controls row's real size across a
        // short burst of separate synchronous layout changes (place tabs
        // re-rendering with a different count, StageTabsScroll's own
        // fit/overflow toggle reacting to that, ...) rather than one clean
        // resize - reacting to every intermediate step separately animated
        // each one in turn, several small height jumps in a row instead of
        // one smooth move to the final height. Coalescing into a single
        // rAF (not a longer setTimeout debounce, which was tried first and
        // left a ~80ms window where the sheet's height was still stale
        // against the row's already-new size - long enough to visibly
        // overlap it) collapses same-tick bursts into one re-apply while
        // keeping the lag to at most a frame.
        let isFirstCallback = true;
        const ro = new ResizeObserver(() => {
            // ResizeObserver always fires once immediately on observe(),
            // reporting the size openFull itself already measured and
            // applied a moment ago - reacting to that first callback too is
            // a same-frame race against that already-correct height, which
            // can restart the CSS transition on an unchanged value and
            // read as a flicker/jump right as the sheet opens.
            if (isFirstCallback) {
                isFirstCallback = false;
                return;
            }
            if (clearanceRafRef.current) cancelAnimationFrame(clearanceRafRef.current);
            clearanceRafRef.current = requestAnimationFrame(() => {
                clearanceRafRef.current = null;
                const next = getFullHeight();
                if (Math.abs(next - heightRef.current) < RESIZE_JUMP_THRESHOLD_PX) return;
                heightRef.current = next;
                applyHeight(next, true);
            });
        });
        ro.observe(el);
        clearanceObserverRef.current = ro;
    }, [getControlsElement, applyHeight, getFullHeight]);

    const unwatchClearance = useCallback(() => {
        if (clearanceObserverRef.current) {
            clearanceObserverRef.current.disconnect();
            clearanceObserverRef.current = null;
        }
        if (clearanceRafRef.current) {
            cancelAnimationFrame(clearanceRafRef.current);
            clearanceRafRef.current = null;
        }
    }, []);

    // Only ever opens to FULL now (there's no PEEK to open "to" instead) -
    // still takes withTransition since the initial mount (already at FULL
    // by the time it's visible, no animation needed) and the expandSignal
    // jump-to-a-specific-match case (also instant, see its own call site)
    // both want false, same as before.
    const openFull = useCallback((withTransition) => {
        const el = sheetRef.current;
        if (!el) return;

        if (savedScrollYRef.current === null) {
            savedScrollYRef.current = window.scrollY;
        }
        window.scrollTo(0, 0);

        const px = getFullHeight();
        heightRef.current = px;
        applyHeight(px, withTransition);
        setFullscreenState(el, true);
        watchClearance();
    }, [applyHeight, getFullHeight, watchClearance]);

    // Callback ref, not a plain useRef + mount useEffect: this sheet's own
    // DOM node can appear well after this hook's first call (isOpen going
    // true is itself what mounts it) - a plain mount effect firing against
    // a still-null sheetRef.current would leave openFull silently no-op'ing
    // on its `if (!el) return` guard. Running this init directly off the
    // node's own attach (and re-running it every time the node reappears -
    // open, close, open again) is what actually guarantees el exists when
    // openFull needs it.
    const setSheetNode = useCallback((node) => {
        sheetRef.current = node;

        if (sheetInitCleanupRef.current) {
            sheetInitCleanupRef.current();
            sheetInitCleanupRef.current = null;
        }
        if (!node) return;

        // The sheet only ever mounts already open - see the hook's own
        // top-of-file comment - so this is always opening to FULL, never a
        // restored PEEK.
        openFull(false);

        sheetInitCleanupRef.current = () => {
            // Same reasoning as unwatchClearance below - the node
            // disappearing (real unmount, or isOpen flipping back to
            // false) is just as much a reason to stop watching the
            // controls row as closing via the handle is.
            unwatchClearance();
            // Every unmount is a close now (there's no more PEEK to
            // collapse to instead) - restore whatever page scroll position
            // openFull saved when the sheet opened, same as the old
            // PEEK-transition branch used to.
            if (savedScrollYRef.current !== null) {
                window.scrollTo(0, savedScrollYRef.current);
                savedScrollYRef.current = null;
            }
            // If the sheet's node disappears while still FULL (a real
            // unmount - navigating away entirely, or the caller closing it
            // some other way than the handle) and this cleanup didn't run,
            // nothing else would ever un-set body.bottom-sheet-full's
            // scroll lock (see CustomModal.css). Left stuck, the page
            // landed on next (e.g. the tournaments list) inherits
            // overflow:hidden on <body> and silently can't be scrolled at
            // all until a hard reload happens to remount and reset it.
            document.body.classList.remove("bottom-sheet-full");
        };
    }, [openFull, unwatchClearance]);

    // Any change to expandSignal (e.g. a new selected match id, bumped on
    // every click even for the same match) pops the sheet to FULL and
    // scrolls to it - including the very first render now: since the sheet
    // only mounts once already triggered open (see the hook's own top-of-
    // file comment), that first render's expandSignal *is* the real click
    // that opened it, not a stale initial value to skip past.
    //
    // Uses the browser's own scrollIntoView(block:"center") rather than a
    // hand-rolled scrollTop calculation against getFullHeight() - that
    // manual math depended on getControlsElement() correctly identifying
    // whichever page control sits lowest, which broke silently in at least
    // one merged-tab layout (Groups embedded under Жеребкування, two
    // "lowest control" candidates coexisting). scrollIntoView needs to
    // measure against the sheet's real FULL-height layout, not its current
    // (possibly still PEEK-sized) one - rather than animate to FULL and
    // wait out that transition first (tried, but the wait needed hiding the
    // sheet meanwhile to avoid a visible snap/slide, which itself read as a
    // laggy blank flash), this skips the height transition entirely for a
    // signal-driven jump (withTransition:false) so the sheet is already at
    // its final size, in one frame, with nothing intermediate to hide or
    // slide away from. Dragging the handle by hand still gets the normal
    // animated snap (see openFull's own withTransition:true calls) -
    // this instant path is only for "jump straight to this specific match".
    // scrollTargetId is read through a ref, not listed as a dependency:
    // consumers recompute it from selectedMatchId, which their own match
    // cards also update on a plain click anywhere inside them (e.g.
    // clicking a score <input> bubbles up to the card's onClick) - not
    // just the deliberate "open this match" click that bumps expandSignal.
    // With scrollTargetId as a dependency, that plain click still changed
    // it (a different id string) and re-ran this whole effect despite
    // expandSignal being unchanged, re-centering/scrolling the sheet out
    // from under the user on every click into an already-open match's
    // inputs - most noticeable on desktop, where clicking a score field
    // doesn't already imply "scroll me into view" the way a touch tap
    // might. The ref always has the latest value when a real expandSignal
    // bump does fire; it just no longer triggers the effect on its own.
    const scrollTargetIdRef = useRef(scrollTargetId);
    scrollTargetIdRef.current = scrollTargetId;

    useEffect(() => {
        if (expandSignal === null || expandSignal === undefined) return;
        openFull(false);

        const targetId = scrollTargetIdRef.current;
        document.getElementById(targetId)?.scrollIntoView({ block: "center", behavior: "auto" });
    }, [expandSignal, openFull]);

    const handleRef = useCallback((node) => {
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        if (!node) return;

        // pointermove/pointerup are listened on window rather than the
        // handle itself, and only for the duration of an active gesture:
        // the handle is a thin strip, so a real drag's pointer leaves its
        // bounds within the first few pixels of movement - without
        // capture, the browser just stops delivering move/up events to it
        // (they go to whatever's now under the cursor instead). Pointer
        // capture (setPointerCapture) is the "proper" fix for this but
        // isn't reliable enough here to depend on alone, so window-level
        // listeners during the drag are the primary mechanism.
        const onWindowPointerMove = (e) => {
            const g = gestureRef.current;
            if (!g || e.pointerId !== g.pointerId) return;
            const el = sheetRef.current;
            // Dragging the handle UP grows the sheet - height moves
            // opposite to clientY, hence startY - clientY (not the other
            // way around, like the old translateY version wanted).
            const delta = g.startY - e.clientY;
            g.maxMovement = Math.max(g.maxMovement, Math.abs(delta));
            const next = Math.min(g.fullHeight, Math.max(PEEK_HEIGHT_PX, g.startHeight + delta));
            heightRef.current = next;
            applyHeight(next, false);
            // Live-toggle the fullscreen (above-header) state as the drag
            // crosses near FULL, not just once it settles there on
            // release - the header should visibly yield as you drag past
            // it, not only after you let go.
            setFullscreenState(el, next >= g.fullHeight - 4);
        };

        const endGesture = (e) => {
            const g = gestureRef.current;
            if (!g || e.pointerId !== g.pointerId) return;
            gestureRef.current = null;
            window.removeEventListener("pointermove", onWindowPointerMove);
            window.removeEventListener("pointerup", endGesture);
            window.removeEventListener("pointercancel", endGesture);

            if (g.maxMovement <= TAP_MAX_MOVEMENT_PX) {
                // A tap: the sheet is only ever mounted already at FULL, so
                // there's nothing to toggle it *to* other than closed.
                swallowGhostClick();
                onClose?.();
                return;
            }

            // A real drag: dragged down closer to the floor than to FULL
            // closes it (same escape hatch as the tap); otherwise snaps
            // back to FULL rather than resting wherever it was released.
            const distanceToFull = Math.abs(heightRef.current - g.fullHeight);
            const distanceToFloor = Math.abs(heightRef.current - PEEK_HEIGHT_PX);
            if (distanceToFloor < distanceToFull) {
                swallowGhostClick();
                onClose?.();
            } else {
                openFull(true);
            }
        };

        const onPointerDown = (e) => {
            if (!sheetRef.current) return;
            gestureRef.current = {
                pointerId: e.pointerId,
                startY: e.clientY,
                startHeight: heightRef.current,
                fullHeight: getFullHeight(),
                maxMovement: 0,
            };
            applyHeight(heightRef.current, false);
            window.addEventListener("pointermove", onWindowPointerMove);
            window.addEventListener("pointerup", endGesture);
            window.addEventListener("pointercancel", endGesture);
        };

        node.addEventListener("pointerdown", onPointerDown);

        cleanupRef.current = () => {
            node.removeEventListener("pointerdown", onPointerDown);
            window.removeEventListener("pointermove", onWindowPointerMove);
            window.removeEventListener("pointerup", endGesture);
            window.removeEventListener("pointercancel", endGesture);
        };
    }, [applyHeight, openFull, getFullHeight, onClose]);

    return { sheetRef: setSheetNode, handleRef };
};
